import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import Footer from '../components/Footer';
import { BookingWidget } from '../components/Booking/BookingWidget';
import { HeroMedia } from '../components/Booking/HeroMedia';

const PricingDialog = dynamic(() => import('../new-design/components/PricingDialog/PricingDialog').then(mod => mod.PricingDialog), { ssr: false });
const TermsDialog = dynamic(() => import('../new-design/components/TermsDialog/TermsDialog').then(mod => mod.TermsDialog), { ssr: false });

export default function IndexPage() {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const [requestedVehicle, setRequestedVehicle] = useState<{ id: string } | null>(null);
  const [heroBookingReady, setHeroBookingReady] = useState(false);
  const markBookingReady = useCallback(() => setHeroBookingReady(true), []);
  const path = Array.isArray(router.query.slug) ? router.query.slug[0] : router.query.slug;
  const pricingOpen = path === 'cennik';
  const termsOpen = path === 'regulamin' || path === 'regulamin-i-polityka-prywatnosci';
  const hashtag = router.asPath.split('#')[1];
  const termsPath = i18n.language.startsWith('pl') ? '/regulamin-i-polityka-prywatnosci' : '/regulamin';
  const title = `QUADTRIP — ${t('booking.title')}`;
  const closeModal = () => { void router.replace('/', undefined, { shallow: true, scroll: false }); };
  const chooseCatalogVehicle = (vehicleId?: string) => {
    setRequestedVehicle(vehicleId ? { id: vehicleId } : null);
    void router.replace('/rezerwacja', undefined, { shallow: true, scroll: false });
  };
  const leavePaymentReturn = () => {
    const query = { ...router.query };
    delete query.payment;
    delete query.reservationId;
    if (query.error === '501') delete query.error;
    return router.replace({ pathname: router.pathname, query }, undefined, { shallow: true, scroll: false });
  };

  useEffect(() => {
    if (path === 'rezerwacja') {
      document.getElementById('booking')?.scrollIntoView({ block: 'start' });
    }
  }, [path]);

  return (
    <div className="quadtrip">
      <Head>
        <title>{title}</title>
        <meta name="description" content={t('booking.subtitle')} key="description" />
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
      </Head>
      <a className="skip-link" href="#booking">{t('booking.skip')}</a>
      <header className="booking-header">
        <Link href="/" className="brand-link" aria-label="QuadTrip">
          <Image src="/images/wypozyczalnia-quadow-szczecin-quadtrip-logo.webp" alt="" width={56} height={56} priority />
          <span>QUADTRIP</span>
        </Link>
        <nav className="header-nav" aria-label={t('booking.navigation')}>
          <a className="desktop-nav" href="#booking">{t('header.reservation')}</a>
          <Link className="desktop-nav" href="/cennik" shallow scroll={false}>{t('header.services')}</Link>
          <a className="desktop-nav" href="#contact">{t('header.contact')}</a>
          <label className="language-select">
            <span className="sr-only">{t('header.language')}</span>
            <select value={router.locale || 'pl'} onChange={event => {
              void router.push({ pathname: router.pathname, query: router.query }, router.asPath,
                { locale: event.target.value, scroll: false });
            }}>
              <option value="pl">PL</option><option value="en">EN</option><option value="de">DE</option>
            </select>
          </label>
        </nav>
      </header>
      <main>
        <div className="booking-layout">
          <section className="booking-intro" aria-labelledby="page-heading">
            <span className="eyebrow">{t('booking.location')}</span>
            <h1 id="page-heading">{t('booking.title')}</h1>
            <p>{t('booking.subtitle')}</p>
            <HeroMedia bookingReady={heroBookingReady} obscured={pricingOpen || termsOpen} />
          </section>
          <BookingWidget requestedVehicle={requestedVehicle} onReady={markBookingReady}
            onLeavePaymentReturn={leavePaymentReturn}
            paymentReturnId={router.query.payment || router.query.error === '501'
              ? typeof router.query.reservationId === 'string' ? router.query.reservationId : null : undefined} />
        </div>
        <section className="product-info" aria-label={t('booking.goodToKnow')}>
          <div className="info-grid">
            <article className="info-card">
              <h2>{t('booking.goodToKnow')}</h2>
              <ul>
                <li>{t('booking.license')}</li>
                <li>{t('booking.depositInfo')}</li>
                <li>{t('booking.fuel')}</li>
              </ul>
              <Link href={termsPath} shallow scroll={false}>{t('header.terms')}</Link>
            </article>
            <article className="info-card">
              <h2>{t('booking.pickup')}</h2>
              <p>{t('footer.address')}</p>
              <p>{t('booking.pickupHint')}</p>
              <a href="#booking-message">{t('booking.messageLabel')}</a>
            </article>
            <article className="info-card">
              <h2>{t('booking.faq')}</h2>
              <details><summary>{t('booking.faqLicense')}</summary><p>{t('booking.license')}</p></details>
              <details><summary>{t('booking.faqMileage')}</summary><p>{t('booking.faqMileageAnswer')}</p></details>
              <details><summary>{t('booking.faqRoute')}</summary><p>{t('booking.faqRouteAnswer')}</p></details>
              <details><summary>{t('booking.faqFees')}</summary><p>{t('booking.faqFeesAnswer')}</p></details>
              <details><summary>{t('booking.faqSharing')}</summary><p>{t('booking.faqSharingAnswer')}</p></details>
              <details><summary>{t('booking.faqDamage')}</summary><p>{t('booking.faqDamageAnswer')}</p></details>
              <details><summary>{t('booking.faqPrice')}</summary><p>{t('booking.quoteMissing')}</p></details>
              <Link href="/cennik" shallow scroll={false}>{t('header.services')}</Link>
            </article>
          </div>
        </section>
      </main>
      <div id="contact"><Footer /></div>
      {pricingOpen && <PricingDialog open handleClose={closeModal} onChooseVehicle={chooseCatalogVehicle} />}
      {termsOpen && <TermsDialog open handleClose={closeModal} hashtag={hashtag} />}
    </div>
  );
}
